import { SEARCH_FILTER_TYPES } from '../config/constants';
/**
 * Get the UI state without page property.
 *
 * @param {SearchParameters} state - The state.
 * @returns {Omit<SearchParameters, 'page'>} State without page.
 */
export function getStateWithoutPage(state) {
  const {
    page,
    ...restProps
  } = state || {};
  return restProps;
}

/**
 * Map a single search filter, like menu or refinementList,
 * to a string.
 *
 * For example:
 * - Menu: '(theme:research)'.
 * - Menu with two attributes: '(theme:research) AND (target_audience:client)'.
 * - RefinementList: '(type:article OR event)'.
 * - Toggle: '(promote:true)'.
 *
 * @param {CombinedSearchFilter} filter - The filter to map.
 * @returns {string} The search filter as a string.
 */
export function mapSearchFilterToString(filter) {
  return Object.entries(filter).map(_ref => {
    let [key, value] = _ref;
    // Value can be a single value or an array of values,
    // so to make sure the next logic will always work,
    // we'll map value to an array if it isn't already.
    const values = Array.isArray(value) ? value : [value];

    // String values that have spaces in them will have to
    // be escaped for Searchkit to parse them correctly.
    const escapedValues = values.map(v => typeof v === 'string' ? v.replace(/ /g, '\\ ') : v);

    // Returns a string in a format like '(type:article OR event)'.
    return "(".concat(key, ":").concat(escapedValues.join(' OR '), ")");
  }).join(' AND ');
}

/**
 * Get search filters from state as a search filters
 * parameter string.
 *
 * For example: '(theme:research) AND (type:article OR event)'.
 *
 * @param {IndexUiState} state - The index UI state.
 * @returns {string} Search filters parameter.
 */
export function getSearchFiltersParameter(state) {
  // Filter out only the filter types we use from the index UI state.
  const filters = Object.fromEntries(Object.entries(state).filter(_ref2 => {
    let [key] = _ref2;
    return SEARCH_FILTER_TYPES.includes(key);
  }));

  // Combine all filter types into one filter string.
  return Object.values(filters).map(filter => mapSearchFilterToString(filter)).join(' AND ');
}